import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { PanelOrder } from '@storeo/api-client';
import { api } from '../lib/api';
import shellStyles from '../components/shell/shell.module.css';
import ui from '../styles/ui.module.css';

const STATUS_LABEL: Record<string, string> = {
  pending: 'Aguardando',
  paid: 'Pago',
  expired: 'Expirado',
  refunded: 'Estornado',
  chargeback: 'Chargeback',
};

const STATUS_PILL: Record<string, string> = {
  pending: 'pillWarn',
  paid: 'pillPos',
  expired: 'pillMuted',
  refunded: 'pillDanger',
  chargeback: 'pillDanger',
};

function money(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const SIZE = 20;

export function Vendas() {
  const qc = useQueryClient();
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const orders = useQuery({
    queryKey: ['orders', page],
    queryFn: () => api.orders({ page, size: SIZE }),
  });

  const refund = useMutation({
    mutationFn: (o: PanelOrder) => api.refundOrder(o.orderId, 'refund'),
    onMutate: (o) => setBusyId(o.orderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível estornar'),
    onSettled: () => setBusyId(null),
  });
  const ship = useMutation({
    mutationFn: (o: PanelOrder) => api.markShipped(o.orderId),
    onMutate: (o) => setBusyId(o.orderId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['orders'] }),
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'Não foi possível marcar como enviado'),
    onSettled: () => setBusyId(null),
  });

  return (
    <>
      <div className={shellStyles.head}>
        <h1>Vendas</h1>
        <p>Os pedidos da sua loja, mais recentes primeiro.</p>
      </div>

      {error ? <p className={ui.error}>{error}</p> : null}

      <div className={ui.card}>
        {orders.isLoading ? (
          <p className={ui.empty}>Carregando…</p>
        ) : !orders.data?.items.length ? (
          <p className={ui.empty}>{page > 0 ? 'Não há mais pedidos.' : 'Nenhuma venda ainda.'}</p>
        ) : (
          <div className={ui.tblScroll}>
            <table className={ui.tbl}>
              <thead>
                <tr>
                  <th>Pedido</th>
                  <th>Comprador</th>
                  <th>Produto</th>
                  <th>Meio</th>
                  <th className="r">Valor</th>
                  <th>Status</th>
                  <th className="r">Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.data.items.map((o) => (
                  <tr key={o.orderId}>
                    <td>
                      <span className={ui.mono}>#{o.number}</span>
                      <div className={ui.faint}>{when(o.createdAt)}</div>
                    </td>
                    <td>
                      <b>{o.customerName || o.customerEmail}</b>
                    </td>
                    <td className={ui.muted}>{o.productName}</td>
                    <td className={ui.mono}>{o.paymentMethod === 'card' ? 'CARTÃO' : 'PIX'}</td>
                    <td className="r">
                      <span className={ui.money}>{money(o.amount)}</span>
                    </td>
                    <td>
                      <span className={`${ui.pill} ${ui[STATUS_PILL[o.status] ?? 'pillMuted']}`}>
                        {STATUS_LABEL[o.status] ?? o.status}
                      </span>
                    </td>
                    <td className="r">
                      <div className={ui.actions} style={{ justifyContent: 'flex-end' }}>
                        {o.status === 'paid' && o.deliveryChannel === 'physical' ? (
                          <button
                            className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
                            disabled={busyId === o.orderId}
                            onClick={() => ship.mutate(o)}
                          >
                            Marcar enviado
                          </button>
                        ) : null}
                        {o.status === 'paid' ? (
                          <button
                            className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
                            disabled={busyId === o.orderId}
                            onClick={() => {
                              setError(null);
                              refund.mutate(o);
                            }}
                          >
                            Estornar
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className={ui.actions}>
        <button
          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
          disabled={page === 0}
          onClick={() => setPage((p) => Math.max(0, p - 1))}
        >
          ← Mais recentes
        </button>
        <button
          className={`${ui.btn} ${ui.btnGhost} ${ui.btnSmall}`}
          disabled={!orders.data?.hasMore}
          onClick={() => setPage((p) => p + 1)}
        >
          Mais antigos →
        </button>
      </div>
    </>
  );
}
